import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  customsDeclarations,
  type CustomsDeclarationItem,
} from '@/api/customsDeclarations';
import { goodsReceipts } from '@/api/goodsReceipts';
import { orgs, nomenclature as nomApi, contractors as contractorsApi } from '@/api/catalogs';
import SearchSelect from '@/components/SearchSelect';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

const FIELDSET_STYLE = { border: 'none', margin: 0, padding: 0, minWidth: 0 } as const;
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  customs_number: string;
  customs_date: string;
  country_origin_code: string;
  customs_fee: string;
  supplier_contractor_id: string;
  settlement_account_code: string;
  vat_account_code: string;
  duty_account_code: string;
  excise_account_code: string;
  declaration_type: 'IMPORT' | 'EXPORT';
  export_confirmation_date: string;  // ISO date or empty
  comment: string;
  items: CustomsDeclarationItem[];
  // Для UI: какое поступление выбрано для добавления строк
  source_receipt_id: string;
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  customs_number: '',
  customs_date: new Date().toISOString().slice(0, 10),
  country_origin_code: '',
  customs_fee: '0',
  supplier_contractor_id: '',
  settlement_account_code: '',
  vat_account_code: '',
  duty_account_code: '',
  excise_account_code: '',
  declaration_type: 'IMPORT',
  export_confirmation_date: '',
  comment: '',
  items: [],
  source_receipt_id: '',
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CustomsDeclarationEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по purchases.receipt.create permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('purchases.receipt.create');
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const docQuery = useQuery({
    queryKey: ['customs-declaration', id],
    queryFn: () => customsDeclarations.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'customs-declaration', (cid) => customsDeclarations.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [basisCleared, setBasisCleared] = useState(false);
  const numberPreview = useNumberPreview('doc.customs_declarations', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  // Надходження-імпорти для вибору (ч.1347: і збережені, ще не проведені —
  // проведення ВМД перевірить підставу саме).
  // Постачальник (0408): вибір → фільтр ЙОГО надходжень для звʼязки.
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const importReceiptsQ = useQuery({
    queryKey: ['goods-receipts-import', form.organization_id, form.supplier_contractor_id],
    queryFn: () => goodsReceipts.list({
      organization_id: form.organization_id, limit: 200,
      contractor_id: form.supplier_contractor_id || undefined,
    }),
    enabled: !!form.organization_id,
  });

  // Когда выбрано поступление — подгружаем его строки
  const sourceReceiptQ = useQuery({
    queryKey: ['goods-receipt-full', form.source_receipt_id],
    queryFn: () => goodsReceipts.get(form.source_receipt_id),
    enabled: !!form.source_receipt_id,
  });

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        customs_number: d.customs_number,
        customs_date: d.customs_date,
        country_origin_code: d.country_origin_code ?? '',
        customs_fee: String(d.customs_fee),
        supplier_contractor_id: (d as { supplier_contractor_id?: string | null })
          .supplier_contractor_id ?? '',
        settlement_account_code: d.settlement_account_code ?? '',
        vat_account_code: d.vat_account_code ?? '',
        duty_account_code: d.duty_account_code ?? '',
        excise_account_code: d.excise_account_code ?? '',
        declaration_type: (d as { declaration_type?: 'IMPORT' | 'EXPORT' }).declaration_type ?? 'IMPORT',
        export_confirmation_date: (d as { export_confirmation_date?: string }).export_confirmation_date ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          goods_receipt_item_id: it.goods_receipt_item_id,
          customs_value: String(it.customs_value),
          duty_amount: String(it.duty_amount),
          excise_amount: String(it.excise_amount),
          vat_amount: String(it.vat_amount),
          vat_rate_id: it.vat_rate_id ?? null,
          fee_allocated: it.fee_allocated,
          cost_addition: it.cost_addition,
        })),
        source_receipt_id: '',
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), source_receipt_id: EMPTY_FORM.source_receipt_id, }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const isEquipmentRow = (itemId: string) =>
    (riCache[itemId]?.account ?? '').startsWith('15');

  // Карта nomenclature_id -> name
  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomList.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomList.data]);

  // Карта goods_receipt_item_id -> { nomenclature_id, quantity } (агрегируем из загруженных
  // поступлений; для уже сохранённого ВМД нужна отдельная подгрузка строк поступлений)
  const [riCache, setRiCache] = useState<Record<string, { nom: string; qty: string; amount: string; account?: string | null }>>({});
  // Вкладки як на надходженні (2026-08-07, рішення власника):
  // Товари/Обладнання (за рахунком рядка надходження 15x) + Рахунки обліку.
  const [viewTab, setViewTab] = useState<'goods' | 'equipment' | 'accounts'>('goods');
  const [receiptPickerOpen, setReceiptPickerOpen] = useState(false);
  // ?receipt=<id> — кнопка «створити ВМД» з імпортного прихода (2026-08-07).
  const basisApplied = useRef(false);
  const basisReceiptId = searchParams.get('receipt');
  useEffect(() => {
    if (!isNew || !basisReceiptId || basisApplied.current) return;
    if (!vatList.data) return;
    basisApplied.current = true;
    void (async () => {
      try {
        const r = await goodsReceipts.get(basisReceiptId);
        setForm((f) => ({ ...f, organization_id: r.organization_id,
          supplier_contractor_id: r.contractor_id }));
        await applyReceipt(basisReceiptId);
      } catch { /* прихід недоступний — юзер обере вручну */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, basisReceiptId, vatList.data]);
  // Обрати надходження з попапа: привʼязка + заповнити ВСІ рядки одразу
  // (2026-08-07, як «заповнити з реалізації» у поверненні).
  async function applyReceipt(receiptId: string) {
    try {
      const receipt = await goodsReceipts.get(receiptId);
      const defaultVat = vatList.data?.find((v) => Number(v.rate_percent) === 20);
      // 2026-08-09 (рішення власника): для ВАЛЮТНОГО прихода фактурна
      // вартість рядка ВМД = грн-еквівалент (номінал × курс прихода).
      const fx = Number(
        (receipt as { exchange_rate?: string | null }).exchange_rate ?? 1) || 1;
      setRiCache((c) => {
        const next = { ...c };
        for (const it of receipt.items) {
          if (it.id) {
            next[it.id] = {
              nom: it.nomenclature_id, qty: it.quantity,
              amount: it.amount_without_vat,
              account: (it as { account_code?: string | null }).account_code ?? null,
            };
          }
        }
        return next;
      });
      setBasisCleared(false);
      setForm((f) => ({
        ...f,
        source_receipt_id: receiptId,
        items: receipt.items.filter((it) => it.id).map((it, idx) => ({
          line_number: idx + 1,
          goods_receipt_item_id: it.id!,
          customs_value: fx !== 1
            ? (Number(it.amount_without_vat) * fx).toFixed(2)
            : it.amount_without_vat,
          duty_amount: '0',
          excise_amount: '0',
          vat_amount: '0',
          vat_rate_id: defaultVat?.id ?? null,
        })),
      }));
      setReceiptPickerOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? String(e));
    }
  }

  useEffect(() => {
    if (sourceReceiptQ.data) {
      setRiCache((c) => {
        const next = { ...c };
        for (const it of sourceReceiptQ.data!.items) {
          if (it.id) {
            next[it.id] = {
              nom: it.nomenclature_id,
              qty: it.quantity,
              amount: it.amount_without_vat,
              account: (it as { account_code?: string | null }).account_code ?? null,
            };
          }
        }
        return next;
      });
    }
  }, [sourceReceiptQ.data]);

  // UX-фікс 2026-07-29: збережена ВМД — дані рядків прихода приходять
  // ЗБАГАЧЕНИМИ з backend (nomenclature_id/receipt_quantity) — UUID не видно.
  useEffect(() => {
    const items = (docQuery.data?.items ?? []) as Array<{
      goods_receipt_item_id: string;
      nomenclature_id?: string | null;
      receipt_quantity?: string | null;
      receipt_account_code?: string | null;
    }>;
    if (!items.length) return;
    setRiCache((c) => {
      const next = { ...c };
      for (const it of items) {
        if (it.nomenclature_id && !next[it.goods_receipt_item_id]) {
          next[it.goods_receipt_item_id] = {
            nom: it.nomenclature_id,
            qty: it.receipt_quantity ?? '',
            amount: '',
            account: it.receipt_account_code ?? null,
          };
        }
      }
      return next;
    });
  }, [docQuery.data]);

  const sumItems = (items: typeof form.items) => {
    let cv = 0, d = 0, e = 0, v = 0, ca = 0;
    for (const it of items) {
      cv += Number(it.customs_value || 0);
      d  += Number(it.duty_amount || 0);
      e  += Number(it.excise_amount || 0);
      v  += Number(it.vat_amount || 0);
      ca += Number(it.cost_addition ?? 0);
    }
    return { cv, d, e, v, ca };
  };
  const totals = useMemo(() => {
    const t = sumItems(form.items);
    const fee = Number(form.customs_fee || 0);
    return { ...t, fee, addition: t.d + t.e + fee };
  }, [form.items, form.customs_fee]);
  // ч.750: «Разом» — по вкладці (товари / обладнання), «Усього» — по документу.
  const tabTotals = useMemo(() => sumItems(form.items.filter((it) =>
    viewTab === 'equipment'
      ? isEquipmentRow(it.goods_receipt_item_id)
      : !isEquipmentRow(it.goods_receipt_item_id))), [form.items, viewTab, riCache]);

  const create = useMutation({
    mutationFn: () => customsDeclarations.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      customs_number: form.customs_number,
      customs_date: form.customs_date,
      country_origin_code: form.country_origin_code || null,
      customs_fee: form.customs_fee || '0',
      supplier_contractor_id: form.supplier_contractor_id || null,
      settlement_account_code: form.settlement_account_code || null,
      vat_account_code: form.vat_account_code || null,
      duty_account_code: form.duty_account_code || null,
      excise_account_code: form.excise_account_code || null,
      declaration_type: form.declaration_type,
      comment: form.comment || null,
      items: form.items.map((it) => ({
        line_number: it.line_number,
        goods_receipt_item_id: it.goods_receipt_item_id,
        customs_value: it.customs_value,
        duty_amount: it.duty_amount,
        excise_amount: it.excise_amount,
        vat_amount: it.vat_amount,
        vat_rate_id: it.vat_rate_id ?? null,
      })),
    } as unknown as Parameters<typeof customsDeclarations.create>[0]),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
      navigate(`/customs-declarations/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => customsDeclarations.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      customs_number: form.customs_number,
      customs_date: form.customs_date,
      country_origin_code: form.country_origin_code || null,
      customs_fee: form.customs_fee || '0',
      comment: form.comment || null,
      items: form.items.map((it) => ({
        line_number: it.line_number,
        goods_receipt_item_id: it.goods_receipt_item_id,
        customs_value: it.customs_value,
        duty_amount: it.duty_amount,
        excise_amount: it.excise_amount,
        vat_amount: it.vat_amount,
        vat_rate_id: it.vat_rate_id ?? null,
      })),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customs-declaration', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await customsDeclarations.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customs_number: form.customs_number,
        customs_date: form.customs_date,
        country_origin_code: form.country_origin_code || null,
        customs_fee: form.customs_fee || '0',
        declaration_type: form.declaration_type,
        comment: form.comment || null,
        items: form.items.map((it) => ({
          line_number: it.line_number,
          goods_receipt_item_id: it.goods_receipt_item_id,
          customs_value: it.customs_value,
          duty_amount: it.duty_amount,
          excise_amount: it.excise_amount,
          vat_amount: it.vat_amount,
          vat_rate_id: it.vat_rate_id ?? null,
        })),
      } as unknown as Parameters<typeof customsDeclarations.create>[0]);
      try {
        await customsDeclarations.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/customs-declarations/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
      navigate(`/customs-declarations/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await customsDeclarations.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customs_number: form.customs_number,
        customs_date: form.customs_date,
        country_origin_code: form.country_origin_code || null,
        customs_fee: form.customs_fee || '0',
        comment: form.comment || null,
        items: form.items.map((it) => ({
          line_number: it.line_number,
          goods_receipt_item_id: it.goods_receipt_item_id,
          customs_value: it.customs_value,
          duty_amount: it.duty_amount,
          excise_amount: it.excise_amount,
          vat_amount: it.vat_amount,
          vat_rate_id: it.vat_rate_id ?? null,
        })),
      });
      await customsDeclarations.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customs-declaration', id] });
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => customsDeclarations.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customs-declaration', id] });
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => customsDeclarations.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
      navigate('/customs-declarations');
    },
  });

  function setItem(i: number, patch: Partial<CustomsDeclarationItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має purchases.receipt.create у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати ВМД у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/customs-declarations')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.866 (новий дизайн, Закон #10): у ВМД «ще дій» не було — права колонка
  // без «Повʼязаних дій» (прогрес / звʼязані / історія).
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  // Звʼязок-підстава: обране у формі АБО (для збереженого дока) з рядків.
  const savedRefs = new Map<string, string>();
  for (const it of (docQuery.data?.items ?? []) as Array<{
    receipt_document_id?: string | null;
    receipt_document_number?: string | null;
  }>) {
    if (it.receipt_document_id && !savedRefs.has(it.receipt_document_id)) {
      savedRefs.set(it.receipt_document_id, it.receipt_document_number ?? '');
    }
  }
  const basisLinkId = form.source_receipt_id
    || (!basisCleared ? [...savedRefs.keys()][0] : '') || '';
  // Прогрес — ЛИШЕ обовʼязкові: організація, дата, номер і дата ВМД (схема
  // вимагає), рядки (без них не проводиться). Постачальник/підстава — ні.
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'customs', title: 'Митне оформлення', required: 2,
      filled: (form.customs_number ? 1 : 0) + (form.customs_date ? 1 : 0) },
    { id: 'party', title: 'Постачальник і підстава', required: 0, filled: 0 },
    { id: 'items', title: 'Товари', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова митна декларація' : `ВМД: ${form.customs_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/customs-declarations')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postDisabled={!isNew && form.items.length === 0}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/customs-declarations/${id}/print`)}
          entriesDoc={{ type: 'doc.customs_declarations', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/customs-declarations/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.customs_declarations', id: isNew ? '' : id! }} />
      }>
      {/* Проведений документ: УСІ поля деактивовано (рішення власника
          2026-08-26). fieldset disabled глушить нативно всі input/select/
          button всередині — включно з тими, де точковий disabled забули.
          Вкладки перегляду і тулбар — ПОЗА fieldset (працюють завжди).
          ч.866: fieldset — усередині кожного блоку (щоб не ламати відступи
          між блоками). */}
      <DocBlock index={1} title="Основні дані">
      <fieldset disabled={isPosted} style={FIELDSET_STYLE}>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа (внутр.)</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{
        marginTop: '0.5rem', padding: '0.5rem', background: 'var(--bg-soft)',
        borderRadius: 6, display: 'flex', gap: '1.5rem', alignItems: 'center',
      }}>
        <strong>Тип ВМД:</strong>
        <label>
          <input type="radio" name="decl_type" value="IMPORT"
                 checked={form.declaration_type === 'IMPORT'}
                 disabled={isPosted || !isNew}
                 onChange={() => setForm({ ...form, declaration_type: 'IMPORT' })} />
          {' '}Імпорт
        </label>
        <label>
          <input type="radio" name="decl_type" value="EXPORT"
                 checked={form.declaration_type === 'EXPORT'}
                 disabled={isPosted || !isNew}
                 onChange={() => setForm({ ...form, declaration_type: 'EXPORT' })} />
          {' '}Експорт
        </label>
        {form.declaration_type === 'EXPORT' && form.export_confirmation_date && (
          <span style={{ color: 'var(--badge-green-fg)', marginLeft: 'auto' }}>
            ✓ Підтверджено: {form.export_confirmation_date}
          </span>
        )}
      </div>
      </fieldset>
      </DocBlock>

      <DocBlock index={2} title="Митне оформлення">
      <fieldset disabled={isPosted} style={FIELDSET_STYLE}>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Номер ВМД (від митниці) *</label>
          <input value={form.customs_number}
                 onChange={(e) => setForm({ ...form, customs_number: e.target.value })}
                 disabled={isPosted}
                 placeholder="напр., UA100000.2026.000001" />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата ВМД *</label>
          <DatePopover value={form.customs_date}
                 onChange={(v) => setForm({ ...form, customs_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label title="Плата митниці за оформлення декларації (1С «Таможенный сбор»). При проведенні розподіляється по рядках пропорційно вартості і йде у собівартість.">
            Збір за оформлення
          </label>
          <input type="number" step="0.01" value={form.customs_fee}
                 onChange={(e) => setForm({ ...form, customs_fee: e.target.value })}
                 disabled={isPosted}
                 style={{ textAlign: 'right' }} />
        </div>
      </div>

      </fieldset>
      </DocBlock>

      <DocBlock index={3} title="Постачальник і підстава">
      <fieldset disabled={isPosted} style={FIELDSET_STYLE}>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 10' }}>
          <label>Постачальник</label>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.supplier_contractor_id}
            onChange={(cid) => setForm({ ...form, supplier_contractor_id: cid, source_receipt_id: '' })}
            disabled={!!form.source_receipt_id}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          {/* 2026-08-07 (рішення власника): як у реалізації — кнопка з попапом
              поступлень; рядки заповнюються всі. ч.866: «Підстава» — назва
              поля, кнопка — лише поки звʼязку немає, × знімає (канон ч.861). */}
          <label>{basisLinkId ? 'Підстава' : '\u00A0'}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {!basisLinkId && (
            <button type="button" className="btn-doc"
              onClick={() => setReceiptPickerOpen(true)}
              disabled={!form.supplier_contractor_id}
              title={!form.supplier_contractor_id
                  ? 'Спершу оберіть постачальника'
                  : 'Обрати проведене надходження: рядки ВМД заповняться з нього'}>
              <DocIcon />створити з надходження
            </button>
            )}
            {basisLinkId && (() => {
              const basis = importReceiptsQ.data?.items.find((x) => x.id === basisLinkId);
              const label = basis
                ? ` №${basis.document_number} від ${new Date(basis.document_date).toLocaleDateString('uk-UA')}`
                : (savedRefs.get(basisLinkId) ? ` №${savedRefs.get(basisLinkId)}` : '');
              return (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                    onClick={() => navigate(`/goods-receipts/${basisLinkId}`)}>
                    надходження{label}
                  </a>
                  {!isPosted && (
                    <button type="button" className="btn-x"
                      onClick={() => { setBasisCleared(true);
                        setForm({ ...form, source_receipt_id: '', items: [] }); }}
                      title="Зняти звʼязок з надходженням і очистити рядки">×</button>
                  )}
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      {receiptPickerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setReceiptPickerOpen(false)}>
          <div className="card" style={{ width: 640, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Проведені надходження постачальника</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setReceiptPickerOpen(false)}>закрити</button>
            </div>
            {(importReceiptsQ.data?.items.length ?? 0) === 0 && (
              <p style={{ color: 'var(--muted)' }}>Немає проведених поступлень цього постачальника.</p>
            )}
            <table className="tch">
              <tbody>
                {importReceiptsQ.data?.items.map((r) => (
                  <tr key={r.id} onClick={() => void applyReceipt(r.id)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-soft)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td>{r.document_number}</td>
                    <td>
                      {new Date(r.document_date).toLocaleDateString('uk-UA')}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {fmt(Number(r.total_amount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </fieldset>
      </DocBlock>

      <DocBlock index={4} title="Товари">
      {/* Вкладки як на надходженні (2026-08-07): Товари/Обладнання +
          Рахунки обліку. Обладнання = рядки надходження з рахунком 15x. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="seg-tabs">
          <button type="button" className={viewTab === 'goods' ? 'active' : ''}
            onClick={() => setViewTab('goods')}>
            Товари ({form.items.filter((it) => !isEquipmentRow(it.goods_receipt_item_id)).length})
          </button>
          <button type="button" className={viewTab === 'equipment' ? 'active' : ''}
            onClick={() => setViewTab('equipment')}>
            Обладнання ({form.items.filter((it) => isEquipmentRow(it.goods_receipt_item_id)).length})
          </button>
        </div>
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
            onClick={() => setViewTab('accounts')}>Рахунки обліку</button>
        </div>
      </div>
      <fieldset disabled={isPosted} style={FIELDSET_STYLE}>
      {viewTab === 'accounts' && (
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '0.75rem', marginTop: '0.5rem' }}>
          <div className="field">
            <label>Розрахунки з митницею</label>
            <input value={form.settlement_account_code} disabled={isPosted}
              placeholder="6851"
              onChange={(e) => setForm({ ...form, settlement_account_code: e.target.value })} />
          </div>
          <div className="field">
            <label>Рахунок ПДВ</label>
            <input value={form.vat_account_code} disabled={isPosted}
              placeholder="6442"
              onChange={(e) => setForm({ ...form, vat_account_code: e.target.value })} />
          </div>
          <div className="field">
            <label>Рахунок мита</label>
            <input value={form.duty_account_code} disabled={isPosted}
              placeholder="(у собівартість)"
              onChange={(e) => setForm({ ...form, duty_account_code: e.target.value })} />
          </div>
          <div className="field">
            <label>Рахунок акцизу</label>
            <input value={form.excise_account_code} disabled={isPosted}
              placeholder="(у собівартість)"
              onChange={(e) => setForm({ ...form, excise_account_code: e.target.value })} />
          </div>
        </div>
      )}

      {viewTab !== 'accounts' && (<>

      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Товар (з надходження)</th>
            {/* ч.750 (термінологія митниці): Митна вартість — БАЗА (фактурна +
                витрати до кордону, гр. 45 МД); мито/акциз рахуються від неї, ПДВ —
                від (митна + мито + акциз). «Разом платежів» — що сплачується
                митниці за рядком; «До собівартості» — мито + акциз + частка збору. */}
            <th className="num" style={{ width: 120 }}>Митна вартість</th>
            <th className="num" style={{ width: 100 }}>Мито</th>
            <th className="num" style={{ width: 100 }}>Акциз</th>
            <th className="num" style={{ width: 100 }}>ПДВ</th>
            <th className="num" style={{ width: 120 }}>Разом платежів</th>
            <th className="num" style={{ width: 130 }}>Загалом</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => ({ it, i })).filter(({ it }) =>
            viewTab === 'equipment'
              ? isEquipmentRow(it.goods_receipt_item_id)
              : !isEquipmentRow(it.goods_receipt_item_id))
            .map(({ it, i }) => {
            const ri = riCache[it.goods_receipt_item_id];
            const label = ri
              ? `${nomName.get(ri.nom) ?? 'товар'} × ${ri.qty}`
              : it.goods_receipt_item_id.slice(0, 8) + '…';
            return (
              <tr key={i}>
                <td>{it.line_number}</td>
                <td style={{ color: 'var(--text-secondary)' }}>{label}</td>
                <td>
                  <input type="number" step="0.01" value={it.customs_value}
                         onChange={(e) => setItem(i, { customs_value: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td>
                  <input type="number" step="0.01" value={it.duty_amount}
                         onChange={(e) => setItem(i, { duty_amount: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td>
                  <input type="number" step="0.01" value={it.excise_amount}
                         onChange={(e) => setItem(i, { excise_amount: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td>
                  <input type="number" step="0.01" value={it.vat_amount}
                         onChange={(e) => setItem(i, { vat_amount: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ textAlign: 'right' }}>
                  {fmt(Number(it.duty_amount || 0) + Number(it.excise_amount || 0) + Number(it.vat_amount || 0))}
                </td>
                {/* ч.751: «Загалом» — митна вартість + мито + акциз + ПДВ. */}
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {fmt(Number(it.customs_value || 0) + Number(it.duty_amount || 0)
                    + Number(it.excise_amount || 0) + Number(it.vat_amount || 0))}
                </td>
                <td>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 500 }}>
            <td colSpan={2} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{fmt(tabTotals.cv)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(tabTotals.d)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(tabTotals.e)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(tabTotals.v)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(tabTotals.d + tabTotals.e + tabTotals.v)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(tabTotals.cv + tabTotals.d + tabTotals.e + tabTotals.v)}</td>
            <td></td>
          </tr>
          <tr>
            <td colSpan={2} style={{ textAlign: 'right' }}>Усього:</td>
            <td style={{ textAlign: 'right' }}>{fmt(totals.cv)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(totals.d)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(totals.e)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(totals.v)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(totals.d + totals.e + totals.v)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(totals.cv + totals.d + totals.e + totals.v)}</td>
            <td></td>
          </tr>
        </tfoot>
      </TchTable>

      </>)}
      </fieldset>
      </DocBlock>

      <DocBlock index={5} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.customs_declarations" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}


    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
